/** One durable record keeps the token pair atomic across reloads and tabs. */
export const SESSION_STORAGE_KEY = 'sciland.session.v1';
const LEGACY_ACCESS_KEY = 'sciland.access_token';
const LEGACY_REFRESH_KEY = 'sciland.refresh_token';

interface StoredSession {
  schema: 1;
  id: string;
  access: string | null;
  refresh: string | null;
  renewedAt: number;
}

let current: StoredSession | null = null;
let initialized = false;
let shared = false;
let lastRaw: string | null = null;
let sessionVersion = 0;
const listeners = new Set<() => void>();
// getRandomValues also works on the HTTP test site (randomUUID requires HTTPS).
const newId = () => crypto.getRandomValues(new Uint32Array(4)).join('-');

function parse(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as StoredSession;
    if (value.schema !== 1 || typeof value.id !== 'string' || !value.id || !Number.isFinite(value.renewedAt)) return null;
    if (value.access === null && value.refresh === null) return value;
    return typeof value.access === 'string' && value.access.trim() && typeof value.refresh === 'string' && value.refresh.trim() ? value : null;
  } catch { return null; }
}

function clearLegacy() {
  try {
    window.sessionStorage.removeItem(LEGACY_ACCESS_KEY);
    window.sessionStorage.removeItem(LEGACY_REFRESH_KEY);
  } catch { /* Storage can be disabled. */ }
}

function notifyExternalChange() {
  // Reads also fence requests before the browser delivers its storage event.
  // Deliver React subscriptions outside the caller's render stack.
  queueMicrotask(() => listeners.forEach(listener => listener()));
}

function read(): StoredSession | null {
  if (typeof window === 'undefined') return current;
  if (!initialized) {
    initialized = true;
    try {
      lastRaw = window.localStorage.getItem(SESSION_STORAGE_KEY);
      current = parse(lastRaw);
      shared = true;
    } catch { shared = false; }
    if (lastRaw !== null) clearLegacy();
    else {
      try {
        const access = window.sessionStorage.getItem(LEGACY_ACCESS_KEY);
        const refresh = window.sessionStorage.getItem(LEGACY_REFRESH_KEY);
        if (access && refresh) current = {schema: 1, id: newId(), access, refresh, renewedAt: 0};
      } catch { /* Fall back to this tab's memory. */ }
    }
    return current;
  }
  if (shared) {
    try {
      const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
      if (raw !== lastRaw) {
        const next = parse(raw);
        const changed = current?.id !== next?.id;
        current = next;
        lastRaw = raw;
        clearLegacy();
        if (changed) { sessionVersion += 1; notifyExternalChange(); }
      }
    } catch { shared = false; }
  }
  return current;
}

function write(value: StoredSession) {
  current = value;
  initialized = true;
  try {
    const raw = JSON.stringify(value);
    window.localStorage.setItem(SESSION_STORAGE_KEY, raw);
    lastRaw = raw;
    shared = true;
  } catch {
    shared = false;
    // Never leave an older durable account behind after a failed replacement.
    try { window.localStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* Memory remains authoritative in this tab. */ }
  }
  clearLegacy();
}

export function assertSessionCurrent(expectedVersion: number): void {
  read();
  if (sessionVersion !== expectedVersion) throw new DOMException('登录会话已切换', 'AbortError');
}

export const tokenStore = {
  getAccessToken: () => read()?.access ?? null,
  getRefreshToken: () => read()?.refresh ?? null,
  hasTokens: () => Boolean(read()?.access && current?.refresh),
  getSessionVersion: () => { read(); return sessionVersion; },
  getRenewedAt: () => read()?.renewedAt ?? 0,
  isShared: () => { read(); return shared; },
  setTokens(access: string, refresh: string) {
    read();
    sessionVersion += 1;
    write({schema: 1, id: newId(), access, refresh, renewedAt: Date.now()});
  },
  rotateTokens(access: string, refresh: string, expectedVersion: number) {
    assertSessionCurrent(expectedVersion);
    if (!current?.refresh) throw new DOMException('登录会话已结束', 'AbortError');
    write({...current, access, refresh, renewedAt: Date.now()});
  },
  /** Called under the refresh lock so simultaneous legacy tabs adopt one session. */
  persistLegacy(expectedVersion: number) {
    assertSessionCurrent(expectedVersion);
    if (current?.refresh && shared && lastRaw === null) write(current);
  },
  clear() {
    read();
    sessionVersion += 1;
    // A credential-free marker prevents stale sessionStorage in another tab
    // from reviving a session that has explicitly been logged out.
    write({schema: 1, id: newId(), access: null, refresh: null, renewedAt: 0});
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    const onStorage = (event: StorageEvent) => {
      if (event.key === SESSION_STORAGE_KEY || event.key === null) read();
    };
    window.addEventListener('storage', onStorage);
    return () => { listeners.delete(listener); window.removeEventListener('storage', onStorage); };
  },
};
